import {
  DndContext,
  DragOverlay,
  closestCorners,
  PointerSensor,
  KeyboardSensor,
  useSensors,
  useSensor,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { invalidateForTask } from "@/lib/useTaskMutations";
import { queryKeys } from "@/lib/queryKeys";
import { TaskCard } from "./TaskCard";
import { useEffortUnit } from "@/lib/effort";
import { WORK_ITEM_TYPES, typeLabel } from "@/lib/workItemType";
import type { WorkItemType } from "@shared/types";
import { toast } from "./Toast";
import { Avatar } from "./Avatar";
import { KanbanColumn } from "./KanbanColumn";
import { KanbanAnnouncer } from "./KanbanAnnouncer";
import {
  canDrop,
  type ActiveTask,
  type OverTarget,
} from "@/lib/kanban/canDrop";
import { moveBetween } from "@/lib/kanban/moveBetween";
import { TASK_STATUS_LABEL } from "@/lib/taskStatus";

export type KanbanTask = {
  id: string;
  title: string;
  /** Non-null for subtasks — those never render as board cards (T42);
   * the parent card shows the N/M progress bar instead. */
  parentTaskId?: string | null;
  status: "backlog" | "todo" | "doing" | "done";
  priority: number;
  type?: WorkItemType;
  epicId?: string | null;
  milestoneId?: string | null;
  sortOrder: number;
  assigneeIds: string[];
  tags?: { id: string; name: string; color: string }[];
  subtaskCount: number;
  subtaskCompletedCount: number;
  commentCount: number;
  attachmentCount: number;
  dueDate: string | null;
  estimate?: number | null;
  isBlocked?: boolean;
};

const COLUMNS = ["backlog", "todo", "doing", "done"] as const;
type ColumnId = (typeof COLUMNS)[number];
type ItemsByStatus = Record<ColumnId, KanbanTask[]>;

const STATUS_META: Record<ColumnId, { label: string; color: string }> = {
  backlog: { label: TASK_STATUS_LABEL.backlog, color: "var(--st-backlog)" },
  todo: { label: TASK_STATUS_LABEL.todo, color: "var(--st-todo)" },
  doing: { label: TASK_STATUS_LABEL.doing, color: "var(--st-doing)" },
  done: { label: TASK_STATUS_LABEL.done, color: "var(--st-done)" },
};

function groupByStatus(tasks: KanbanTask[]): ItemsByStatus {
  const m: ItemsByStatus = { backlog: [], todo: [], doing: [], done: [] };
  for (const t of tasks) {
    // Coerce unknown statuses to "backlog" so a single bad row doesn't crash
    // the whole board. Server data may drift outside the enum (legacy rows,
    // new statuses introduced server-side first); dropping the task entirely
    // would be worse than parking it in backlog where the user can resolve it.
    const col: ColumnId = (COLUMNS as readonly string[]).includes(t.status)
      ? (t.status as ColumnId)
      : "backlog";
    m[col].push(t);
  }
  for (const s of COLUMNS) m[s].sort((a, b) => a.sortOrder - b.sortOrder);
  return m;
}

export type KanbanBoardProps = {
  projectId: string;
  tasks: KanbanTask[];
  /** Map of userId -> { name, image? } for avatar resolution */
  users?: Record<string, { name: string; image?: string | null }>;
  /** Render lanes by assignee, work-item type, or epic instead of status columns */
  groupBy?: "status" | "assignee" | "type" | "epic";
  /** epicId -> display name, for the epic grouping lane labels */
  epicNames?: ReadonlyMap<string, string>;
  /** Called when the user clicks the + button in a column head or the "Add task" footer button */
  onAddTaskInColumn?: (status: ColumnId) => void;
};

export function KanbanBoard({
  projectId,
  tasks,
  users = {},
  groupBy = "status",
  epicNames,
  onAddTaskInColumn,
}: KanbanBoardProps) {
  const qc = useQueryClient();
  const effortUnit = useEffortUnit();
  const [dragOverColumnId, setDragOverColumnId] = useState<string | null>(null);
  // Track the actively dragged task so we can render it inside <DragOverlay>.
  // The overlay is mounted at DndContext root, which escapes the column's
  // overflow-y: auto clipping that previously cut the dragged card off at
  // the source-column boundary.
  const [activeTask, setActiveTask] = useState<KanbanTask | null>(null);

  // T42: subtasks never render as independent board cards. The parent card
  // already carries the N/M progress bar; the full list lives on task detail.
  const topLevelTasks = useMemo(
    () => tasks.filter((t) => t.parentTaskId == null),
    [tasks],
  );

  // Items lifted into local state so onDragOver can shift siblings (creating
  // the insertion gap). Re-syncs from the upstream `tasks` prop whenever
  // we are NOT mid-drag — otherwise a query refetch would clobber our snapshot.
  const [items, setItems] = useState<ItemsByStatus>(() =>
    groupByStatus(topLevelTasks),
  );
  const isDraggingRef = useRef(false);
  useEffect(() => {
    if (!isDraggingRef.current) setItems(groupByStatus(topLevelTasks));
  }, [topLevelTasks]);

  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState("");
  const snapshotRef = useRef<ItemsByStatus | null>(null);

  const byAssignee = useMemo(() => {
    if (groupBy !== "assignee") return null;
    const m = new Map<string, KanbanTask[]>();
    for (const t of topLevelTasks) {
      const ids = t.assigneeIds.length > 0 ? t.assigneeIds : ["__unassigned__"];
      for (const id of ids) {
        const arr = m.get(id) ?? [];
        arr.push(t);
        m.set(id, arr);
      }
    }
    return m;
  }, [topLevelTasks, groupBy]);

  const byType = useMemo(() => {
    if (groupBy !== "type") return null;
    const m = new Map<WorkItemType, KanbanTask[]>();
    for (const wt of WORK_ITEM_TYPES) m.set(wt, []);
    for (const t of topLevelTasks) {
      const wt = (t.type ?? "task") as WorkItemType;
      (m.get(wt) ?? m.set(wt, []).get(wt)!).push(t);
    }
    // Drop empty lanes so a board with no bugs doesn't show an empty Bug lane.
    return new Map([...m.entries()].filter(([, ts]) => ts.length > 0));
  }, [topLevelTasks, groupBy]);

  const byEpic = useMemo(() => {
    if (groupBy !== "epic") return null;
    const m = new Map<string, KanbanTask[]>();
    for (const t of topLevelTasks) {
      const key = t.epicId ?? "__none__";
      const arr = m.get(key) ?? [];
      arr.push(t);
      m.set(key, arr);
    }
    return m;
  }, [topLevelTasks, groupBy]);

  // Custom mutation rather than usePatchTask: we need the optimistic-update
  // + rollback lifecycle (onMutate/onError context) that the generic hook
  // doesn't expose. We still route through `invalidateForTask` so the
  // post-success invalidation set stays in sync with everywhere else.
  const patch = useMutation({
    mutationFn: ({
      taskId,
      status,
      prevId,
      nextId,
    }: {
      taskId: string;
      status?: KanbanTask["status"];
      prevId: string | null;
      nextId: string | null;
    }) => api.patchTask(taskId, { status, prevId, nextId }),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: queryKeys.tasksFor(projectId) });
      const prev = qc.getQueryData<{ tasks: KanbanTask[] }>(
        queryKeys.tasksFor(projectId),
      );
      if (v.status) {
        qc.setQueryData<{ tasks: KanbanTask[] }>(
          queryKeys.tasksFor(projectId),
          (old) => {
            if (!old) return old;
            const next = old.tasks.map((t) =>
              t.id === v.taskId
                ? { ...t, status: v.status as KanbanTask["status"] }
                : t,
            );
            return { tasks: next };
          },
        );
      }
      return { prev };
    },
    onError: (_e, v, ctx) => {
      if (ctx?.prev) qc.setQueryData(queryKeys.tasksFor(projectId), ctx.prev);
      toast.error("Couldn't move task", {
        action: { label: "Retry", onClick: () => patch.mutate(v) },
      });
    },
    onSettled: (_d, _e, v) =>
      invalidateForTask(qc, { projectId, taskId: v.taskId }),
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  function findContainer(taskId: string): ColumnId | null {
    for (const s of COLUMNS)
      if (items[s].some((t) => t.id === taskId)) return s;
    return null;
  }

  function onDragStart(event: DragStartEvent) {
    isDraggingRef.current = true;
    const id = event.active.id as string;
    const task = topLevelTasks.find((t) => t.id === id) ?? null;
    setActiveTask(task);
    snapshotRef.current = items;
    setDragOverColumnId(findContainer(id));
    if (task) {
      setAnnouncement(`Picked up ${task.title}. Currently in ${task.status}.`);
    }
  }

  function onDragOver(event: DragOverEvent) {
    const { active, over } = event;
    if (!over) {
      setDragOverColumnId(null);
      return;
    }
    const activeId = active.id as string;
    const overId = over.id as string;

    const sourceCol = findContainer(activeId);
    // Over a column directly (empty-column drop): use it as the destination.
    // Over a card: derive the destination from the card's container.
    const destCol = (COLUMNS as readonly string[]).includes(overId)
      ? (overId as ColumnId)
      : findContainer(overId);
    if (!sourceCol || !destCol) return;

    setDragOverColumnId(destCol);

    if (sourceCol === destCol && activeId === overId) return;

    setItems((prev) => moveBetween(prev, activeId, sourceCol, destCol, overId));
  }

  function onDragEnd(event: DragEndEvent) {
    isDraggingRef.current = false;
    setDragOverColumnId(null);
    const { active, over } = event;
    const activeId = active.id as string;
    const task = activeTask;
    const snapshot = snapshotRef.current;
    snapshotRef.current = null;
    setActiveTask(null);

    if (!task || !snapshot) return;

    // Dropped outside any droppable — treat as cancel: revert and bail.
    if (!over) {
      setItems(snapshot);
      setAnnouncement(
        `Dropped outside. ${task.title} returned to ${task.status}.`,
      );
      return;
    }

    // findContainer reads from the *current* items state — which onDragOver
    // has already mutated so the dragged card now lives in the destination
    // column. So this returns the destination, not the source.
    const destCol = findContainer(activeId);
    if (!destCol) return;

    const destIdx = items[destCol].findIndex((t) => t.id === activeId);
    const overTarget: OverTarget = { columnId: destCol, index: destIdx };
    const verdict = canDrop(
      {
        id: task.id,
        status: task.status,
        subtaskCount: task.subtaskCount,
        subtaskCompletedCount: task.subtaskCompletedCount,
      } satisfies ActiveTask,
      overTarget,
    );
    const allowed = verdict === true;
    if (!allowed) {
      const reason =
        typeof verdict === "object" ? verdict.reason : "Move not allowed.";
      setItems(snapshot);
      setRejectingId(task.id);
      toast.error(reason);
      setTimeout(() => setRejectingId(null), 500);
      setAnnouncement(
        `Move rejected. ${task.title} returned to ${task.status}.`,
      );
      return;
    }

    // Diff snapshot vs items to decide what to persist. The original source
    // is where the snapshot remembered the card living.
    const fromCol = (Object.keys(snapshot) as ColumnId[]).find((c) =>
      snapshot[c].some((t) => t.id === task.id),
    )!;
    const fromIdx = snapshot[fromCol].findIndex((t) => t.id === task.id);

    if (fromCol === destCol && fromIdx === destIdx) {
      setAnnouncement(`Dropped ${task.title} back in ${destCol}. No change.`);
      return;
    }

    // Server takes prevId/nextId (not sortOrder) — derive neighbours from the
    // destination column. The dragged card already occupies destIdx in items,
    // so its neighbours are at destIdx-1 and destIdx+1.
    const destList = items[destCol];
    const prevId = destIdx > 0 ? destList[destIdx - 1]!.id : null;
    const nextId =
      destIdx < destList.length - 1 ? destList[destIdx + 1]!.id : null;

    patch.mutate({
      taskId: task.id,
      status: fromCol !== destCol ? destCol : undefined,
      prevId,
      nextId,
    });
    setAnnouncement(`Dropped ${task.title} into ${destCol}.`);
  }

  function onDragCancel() {
    isDraggingRef.current = false;
    setDragOverColumnId(null);
    if (snapshotRef.current) setItems(snapshotRef.current);
    snapshotRef.current = null;
    if (activeTask) {
      setAnnouncement(
        `Cancelled. ${activeTask.title} returned to ${activeTask.status}.`,
      );
    }
    setActiveTask(null);
  }

  // Assignee lane mode — read-only columns (DnD mutating assignee_id on drop
  // is non-trivial; lanes are read-only for now).
  if (groupBy === "assignee" && byAssignee) {
    return (
      <div className="board lanes">
        {Array.from(byAssignee.entries()).map(([assigneeId, laneTasks]) => {
          const user =
            assigneeId === "__unassigned__"
              ? { name: "Unassigned" }
              : (users[assigneeId] ?? { name: assigneeId });
          return (
            <div key={assigneeId} className="lane">
              <div className="lane-head">
                {assigneeId !== "__unassigned__" && (
                  <Avatar
                    id={assigneeId}
                    name={user.name}
                    size={28}
                    image={users[assigneeId]?.image}
                  />
                )}
                <div>
                  <div className="ln-name">{user.name}</div>
                  <div className="ln-sub">
                    {laneTasks.length} task{laneTasks.length === 1 ? "" : "s"}
                  </div>
                </div>
              </div>
              <div className="lane-tasks">
                {laneTasks.map((t) => (
                  // Wrap in a minimal DndContext so useSortable inside TaskCard
                  // doesn't crash without a parent context. Lane tasks are
                  // display-only (no sorting across lanes).
                  <DndContext key={t.id} sensors={sensors}>
                    <SortableContext
                      items={[t.id]}
                      strategy={verticalListSortingStrategy}
                    >
                      <TaskCard
                        task={t}
                        users={users}
                        effortUnit={effortUnit}
                      />
                    </SortableContext>
                  </DndContext>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  if (groupBy === "epic" && byEpic) {
    return (
      <div className="board lanes">
        {Array.from(byEpic.entries()).map(([epicId, laneTasks]) => (
          <div key={epicId} className="lane">
            <div className="lane-head">
              <div>
                <div className="ln-name">
                  {epicId === "__none__"
                    ? "No epic"
                    : (epicNames?.get(epicId) ?? "Epic")}
                </div>
                <div className="ln-sub">
                  {laneTasks.length} item{laneTasks.length === 1 ? "" : "s"}
                </div>
              </div>
            </div>
            <div className="lane-tasks">
              {laneTasks.map((t) => (
                <DndContext key={t.id} sensors={sensors}>
                  <SortableContext
                    items={[t.id]}
                    strategy={verticalListSortingStrategy}
                  >
                    <TaskCard task={t} users={users} effortUnit={effortUnit} />
                  </SortableContext>
                </DndContext>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (groupBy === "type" && byType) {
    return (
      <div className="board lanes">
        {Array.from(byType.entries()).map(([wt, laneTasks]) => (
          <div key={wt} className="lane">
            <div className="lane-head">
              <div>
                <div className="ln-name">{typeLabel(wt)}</div>
                <div className="ln-sub">
                  {laneTasks.length} item{laneTasks.length === 1 ? "" : "s"}
                </div>
              </div>
            </div>
            <div className="lane-tasks">
              {laneTasks.map((t) => (
                <DndContext key={t.id} sensors={sensors}>
                  <SortableContext
                    items={[t.id]}
                    strategy={verticalListSortingStrategy}
                  >
                    <TaskCard task={t} users={users} effortUnit={effortUnit} />
                  </SortableContext>
                </DndContext>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
      onDragCancel={onDragCancel}
      autoScroll={{
        acceleration: 8,
        threshold: { x: 0.15, y: 0.1 },
      }}
      accessibility={{
        announcements: {
          onDragStart: ({ active }) => `Picked up task ${active.id}.`,
          onDragOver: ({ active, over }) =>
            over
              ? `Task ${active.id} is over ${over.id}.`
              : `Task ${active.id} is no longer over a droppable area.`,
          onDragEnd: ({ active, over }) =>
            over
              ? `Task ${active.id} was dropped on ${over.id}.`
              : `Task ${active.id} was dropped.`,
          onDragCancel: ({ active }) =>
            `Dragging was cancelled. Task ${active.id} was returned.`,
        },
      }}
    >
      <div className="board-scroll" role="region" aria-label="Project board">
        <div className="board">
          {COLUMNS.map((status) => (
            <KanbanColumn
              key={status}
              status={status}
              label={STATUS_META[status].label}
              color={STATUS_META[status].color}
              tasks={items[status]}
              users={users}
              isDropTarget={dragOverColumnId === status}
              rejectingId={rejectingId}
              onAddTask={onAddTaskInColumn}
              effortUnit={effortUnit}
            />
          ))}
        </div>
      </div>
      {/* DragOverlay renders the dragged card outside any column's DOM tree,
          so the source column's overflow-y: auto no longer clips it at the
          column boundary. */}
      <DragOverlay
        dropAnimation={{
          duration: 200,
          easing: "cubic-bezier(0.18, 0.67, 0.6, 1.22)",
        }}
      >
        {activeTask ? (
          <div style={{ cursor: "grabbing" }}>
            <TaskCard
              task={activeTask}
              users={users}
              isDragOverlay
              effortUnit={effortUnit}
            />
          </div>
        ) : null}
      </DragOverlay>
      <KanbanAnnouncer message={announcement} />
    </DndContext>
  );
}
